import React, { FC, useState } from "react";
import { Grid, GridRow } from "@getregistered/greg-editors";
import {
  gridLoader,
  GridLoadSuccessResponse,
} from "../../../domain/grid/resultLoader";
import { AsyncDownloadLink, PaginatedApplicationPrimerGrid } from "../PaginatedApplicationPrimerGrid";
import { ResetButtonPrimer } from "./ResetButtonPrimer";
import { PrintButtonPrimer } from "./PrintButtonPrimer";
import railsFetch from "../../railsFetch";
import { TemplateSelectPrimer } from "./TemplateSelectPrimer";
import { TotalPrintedPrimer } from "./TotalPrintedPrimer";
import { Box, Stack } from "@primer/react";

export interface PaginatedNameBadgePrimerGridProps {
  grid: Grid;
  templates: string[];
  defaultTemplate: string;
  resultsUrl: string;
  totalsUrl: string;
  printUrl: string;
  resetUrl: string;
  csvUrl?: string;
  xlsxUrl?: string;
  eventId?: string;
  gridType?: string;
  reportExportsUrl?: string;
  showResetAll?: boolean;
  batchSize: number;
  // GR-1425: gates the Template/Reset/Print UI. View-only roles
  // (badge_printing:view without :print) get canPrint=false.
  canPrint?: boolean;
}

export interface NameBadgeRecordsResponse extends GridLoadSuccessResponse {
  totalPrinted: number;
  totalPrints?: number;
  totalReprints?: number;
}

export const PaginatedNameBadgePrimerGrid: FC<PaginatedNameBadgePrimerGridProps> = ({
  grid,
  templates,
  defaultTemplate,
  resultsUrl,
  totalsUrl,
  printUrl,
  resetUrl,
  csvUrl,
  xlsxUrl,
  eventId,
  gridType,
  reportExportsUrl,
  showResetAll,
  batchSize,
  canPrint = true,
}) => {
  const [cacheVersion, setCacheVersion] = useState<number>(1);
  const [selectedItems, setSelectedItems] = useState<GridRow[] | undefined>();
  const [template, setTemplate] = useState<string>(defaultTemplate);
  const [currentResponse, setCurrentResponse] =
    useState<NameBadgeRecordsResponse | undefined>();

  const loader = gridLoader<NameBadgeRecordsResponse>(
    resultsUrl,
    totalsUrl,
    grid.columns,
    setCurrentResponse
  );

  const printUrlWithQuery = (): string => {
    if (selectedItems?.length === 0 || selectedItems === undefined) {
      return `${printUrl}?template=${template}`;
    } else {
      const selectedItemsQuery = () =>
        selectedItems.map((i) => `id[]=${i.id}`).join("&");
      return `${printUrl}?${selectedItemsQuery()}&template=${template}`;
    }
  };

  const resetUrlWithQuery = (): string => {
    if (selectedItems?.length === 0 || selectedItems === undefined) {
      return resetUrl;
    } else {
      const selectedItemsQuery = () =>
        selectedItems.map((i) => `id[]=${i.id}`).join("&");
      return `${resetUrl}?${selectedItemsQuery()}`;
    }
  };

  const handlePrint = async () => {
    const pdfWindow = window.open("about:blank", "_blank");
    const url = printUrlWithQuery();
    try {
      const response = await railsFetch(url, { method: "POST" });
      const result = (await response.clone().json().catch(() => ({}))) as {
        printed_ids?: number[];
        blocked?: { id: number; reason: string }[];
      };
      const location = response.headers.get("Location");
      setCacheVersion(cacheVersion + 1);

      if (result.blocked && result.blocked.length > 0) {
        const reasons = result.blocked
          .map((b) => `#${b.id}: ${b.reason.replace(/_/g, " ")}`)
          .join("\n");
        alert(`${result.blocked.length} badge(s) were not printed:\n${reasons}`);
      }

      if (pdfWindow && location && result.printed_ids && result.printed_ids.length > 0) {
        pdfWindow.location.href = location;
      } else if (pdfWindow) {
        pdfWindow.close();
      }
    } catch (error) {
      pdfWindow?.close();
      throw error;
    }
  };

  const handleReset = async () => {
    const url = resetUrlWithQuery();
    await railsFetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });
    setSelectedItems([]);
    setCacheVersion(cacheVersion + 1);
  };

  return (
    <>
      <Box className="name-badge-grid-container">
        {currentResponse !== undefined && (
          <Box className="name-badge-grid-header">
            <Box className="name-badge-grid-left">
              {canPrint && (
                <TemplateSelectPrimer
                  templates={templates}
                  selectedTemplate={template}
                  onChange={setTemplate}
                />
              )}
              <TotalPrintedPrimer
                totalPrinted={currentResponse.totalPrinted}
                totalItems={currentResponse.totalResults}
                totalPrints={currentResponse.totalPrints}
                totalReprints={currentResponse.totalReprints}
              />
            </Box>
            {canPrint && (
              <Box className="name-badge-grid-right">
                <ResetButtonPrimer
                  selectedItems={selectedItems}
                  onClick={handleReset}
                  showResetAll={showResetAll}
                />
                <PrintButtonPrimer
                  selectedItems={selectedItems}
                  remainingItemsCount={
                    currentResponse.totalResults - currentResponse.totalPrinted
                  }
                  batchSize={batchSize}
                  onClick={handlePrint}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
      <PaginatedApplicationPrimerGrid
        grid={grid}
        loader={loader}
        page={1}
        defaultPerPage={batchSize}
        resultsCacheKey={cacheVersion}
        onSelectionChange={setSelectedItems}
        showActions={false}
        actionCellRenderer="menu"
        asyncDownloadLinks={eventId && gridType && reportExportsUrl ? [
          { title: "Download CSV", eventId, gridType, exportType: "csv" as const, reportExportsUrl },
          { title: "Download XLSX", eventId, gridType, exportType: "xlsx" as const, reportExportsUrl },
        ] : undefined}
        downloadLinks={!(eventId && reportExportsUrl) ? [
          ...(csvUrl ? [{ title: "Download CSV", baseUrl: csvUrl }] : []),
          ...(xlsxUrl ? [{ title: "Download XLSX", baseUrl: xlsxUrl }] : []),
        ] : undefined}
      />
    </>
  );
}; 